import React, {useEffect, useState} from "react";
import {useStores} from "../../../../stores/useStores";
import {observer} from "mobx-react-lite";
import {useAsync} from "react-async";
import CreateEditEventTemplate from "../../../../components/templates/events/createEditEventTemplate";
import {useTranslation} from "react-i18next";
import {runInAction} from "mobx";
import {ToastMessageStatus} from "../../../../components/atoms/toastMessage/toastMessage";
import EventTemplatesModal from "../../../../components/organisms/eventTemplatesModal/eventTemplatesModal";
import EventTemplateCategory from "../../../../stores/domainStore/pages/models/eventTemplateCategory";
import {EventTemplateStatus} from "../../../../shared/enums/eventTemplateStatus";
import {EventCatalogueSortField} from "../../../../shared/enums/eventCatalogueSortField";
import {useHistory} from "react-router";
import {RoutePaths} from "../../../../shared/routePaths";
import {Navigation} from "../../../../utils/navigation";
import {QueryParams} from "../../../../shared/enums/queryParams";
import PreReleaseConfirmationModal from "../../../../components/organisms/preReleaseConfirmationModal/preReleaseConfirmationModal";
import {format} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";
import InReleaseCalendarConfirmationModal from "../../../../components/organisms/inReleaseCalendarConfirmationModal/inReleaseCalendarConfirmationModal";

// testing this component is too much like integration as opposed to unit
/* istanbul ignore file */

export interface ICreateEventProps {}

const CreateEvent: React.FC<ICreateEventProps> = (props) => {
  const [hasData, setHasData] = useState(false);
  const [selectedTemplateCategories, setSelectedTemplateCategories] = useState<
    EventTemplateCategory[]
  >([]);
  const {
    uiStore: {
      modalStore,
      eventsUiStore,
      eventCatalogueUiStore,
      toastMessageStore
    },
    domainStore: {eventsDomainStore, eventCatalogueDomainStore}
  } = useStores();

  useEffect(() => {
    setSelectedTemplateCategories(
      eventCatalogueDomainStore.eventTemplateCategories
    );
  }, [eventCatalogueDomainStore.eventTemplateCategories]);

  useEffect(() => {
    eventsDomainStore.setIsMultiVillageEvent();
  }, [eventsDomainStore, eventsDomainStore.editableEvent]);

  const history = useHistory();

  const selectedCalendarName = Navigation.getQueryParamByKey(
    history,
    QueryParams.calendar
  );

  useEffect(() => {
    eventsDomainStore.setOccurrenceDate(null);
    eventsUiStore.setEditingEventSeries(false);

    runInAction(() => {
      eventsUiStore.selectedCalendarName = selectedCalendarName;
    });
  }, [eventsDomainStore, eventsUiStore, selectedCalendarName]);

  const [
    isPreReleaseConfirmationModalOpen,
    setIsPreReleaseConfirmationModalOpen
  ] = useState(false);

  const [
    isInReleaseCalendarConfirmationModalOpen,
    setInReleaseCalendarConfirmationModalOpen
  ] = useState(false);

  const saveEvent = useAsync({
    deferFn: eventsDomainStore.createEvent,
    onReject: (error) => {
      // eslint-disable-next-line no-console
      console.error("[saveEvent] onReject", error);
      toastMessageStore.displayToastMessage(
        t("event:messages.createEventFailure", {
          eventTitle: eventsDomainStore.editableEvent.title.text
        }),
        ToastMessageStatus.Danger
      );
    },
    onResolve: (eventTitle) => {
      toastMessageStore.displayToastMessage(
        t("event:messages.createdNewEvent", {eventTitle: eventTitle}),
        ToastMessageStatus.Success
      );
      Navigation.go(history, RoutePaths.Events);
    }
  });

  const {t} = useTranslation(["translation", "event"]);

  const loadEventCategory = useAsync({
    deferFn: (args) => {
      return eventCatalogueUiStore.loadSelectedEventCategory(
        args[0],
        EventTemplateStatus.Active,
        eventsUiStore.calendarTypeBySelectedCalendarName
      );
    },
    onResolve: () => {
      setSelectedTemplateCategories([
        eventCatalogueDomainStore.eventTemplateCategory
      ]);
      setHasData(true);
    },
    onReject: (error) => {
      modalStore.openErrorModal(error);
    }
  });

  const doTemplateCategoryChanged = (id: string): void => {
    if (id === "") {
      loadEventTemplateCategories.run();
    } else {
      loadEventCategory.run(id);
    }
  };

  const loadEventTemplateCategories = useAsync({
    deferFn: (args) => {
      return eventCatalogueUiStore.loadEventCatalogue(
        EventTemplateStatus.Active,
        3,
        EventCatalogueSortField.lastUsed,
        "",
        true,
        eventsUiStore.calendarTypeBySelectedCalendarName
      );
    },
    onResolve: () => {
      setHasData(true);
    },
    onReject: (error) => {
      modalStore.openErrorModal(error);
    }
  });

  const openEventTemplateModal = () => {
    runInAction(() => (eventCatalogueUiStore.isModalOpen = true));
    loadEventTemplateCategories.run();
  };

  const inReleasedCalendar = () => {
    const eventStartDate =
      eventsDomainStore.editableEvent.eventDateDetails.startDate.date;
    const releasePeriodStartDate = eventsUiStore.calendarSchedule?.startDate;

    if (!eventStartDate || !releasePeriodStartDate) {
      return false;
    }

    return (
      utcToZonedTime(eventStartDate, eventsDomainStore.editableEvent.timeZone) <
      releasePeriodStartDate
    );
  };

  const publishEvent = () => {
    void (async () => {
      if (!eventsUiStore.calendarSchedule?.startDate) {
        await eventsUiStore.loadEventReleaseDate();
      }

      if (inReleasedCalendar()) {
        setInReleaseCalendarConfirmationModalOpen(true);
      } else if (eventsDomainStore.editableEvent.releaseDate?.date !== null) {
        setIsPreReleaseConfirmationModalOpen(true);
      } else {
        saveEvent.run();
      }
    })();
  };

  return (
    <>
      {eventsDomainStore.editableEvent.releaseDate.date && (
        <PreReleaseConfirmationModal
          toggle={() => setIsPreReleaseConfirmationModalOpen(false)}
          isOpen={isPreReleaseConfirmationModalOpen}
          onPublishBtnClicked={() => {
            saveEvent.run();
            setIsPreReleaseConfirmationModalOpen(false);
          }}
          isSaving={saveEvent.isPending}
          preReleaseDate={format(
            utcToZonedTime(
              eventsDomainStore.editableEvent.releaseDate.date!,
              eventsDomainStore.editableEvent.timeZone
            ),
            "dd/MM/yyyy"
          )}
        />
      )}

      {eventsDomainStore.editableEvent && (
        <InReleaseCalendarConfirmationModal
          eventDetails={eventsDomainStore.editableEvent}
          toggle={() => setInReleaseCalendarConfirmationModalOpen(false)}
          isOpen={isInReleaseCalendarConfirmationModalOpen}
          onPublishBtnClicked={() => {
            saveEvent.run();
            setInReleaseCalendarConfirmationModalOpen(false);
          }}
          isEnhanced={eventsUiStore.isEnhancedCalendar}
          isSaving={saveEvent.isPending}
        />
      )}

      <CreateEditEventTemplate
        isSaving={saveEvent.isPending}
        isEditing={false}
        isDeleting={false}
        publishAction={publishEvent}
        deleteAction={() => {}}
        isLoadingEventDetails={eventsUiStore.isLoading}
        selectedEventTemplateRef={
          eventCatalogueDomainStore.selectedEventTemplateRef
        }
        onChangeTemplate={() => {
          eventsDomainStore.editableEvent.hasChanges
            ? modalStore.openModalWithContent({
                content: t("event:messages.changeTemplate"),
                title: t("event:labels.changeTemplate"),
                primaryButtonText: t("event:actions.affirmativeYesChange"),
                secondaryButtonText: t("translation:confirmations.negative"),
                centered: true,
                dangerButton: true,
                onPrimaryClick: () => {
                  eventsDomainStore.editableEvent.setIsDirty(false);
                  openEventTemplateModal();
                  modalStore.toggle();
                },
                onSecondaryClick: modalStore.toggle
              })
            : openEventTemplateModal();
        }}
      />
      <EventTemplatesModal
        isOpen={eventCatalogueUiStore.isModalOpen}
        isLoading={eventCatalogueUiStore.isLoading}
        hideFeature={true}
        toggle={() => {
          runInAction(() => (eventCatalogueUiStore.isModalOpen = false));
          setHasData(false);
        }}
        eventTemplateCategories={
          eventCatalogueDomainStore.eventTemplateCategories
        }
        selectedTemplateCategories={selectedTemplateCategories}
        onTemplateCategoryChanged={doTemplateCategoryChanged}
        onUseTemplate={(id: string) => {
          eventCatalogueUiStore
            .loadEventTemplate(id)
            .catch((error) => modalStore.openErrorModal(error));
          runInAction(() => (eventCatalogueUiStore.isModalOpen = false));
          Navigation.navigateWithParams(
            history,
            RoutePaths.CreateEvent,
            QueryParams.TemplateId,
            id
          );
        }}
        hasData={hasData}
      />
    </>
  );
};

export default observer(CreateEvent);
